<template>
    <div v-if="content"
         ref="rootRef"
         class="bg-gray-300 pb-1">
        <ProductGuideBlock v-if="contentForProductGuide" :content="contentForProductGuide" :is-first-block="true"/>
        <CompatabilityGuideBlock v-else-if="isCompatibilityGuide" :content="content" :is-first-block="true"/>
        <RackConfiguratorPage v-else-if="isRackConfiguratorGuide && additionalContent" :content="additionalContent">
            <template #externalRackGuideSummary="{ configurationId }">
                <div>
                    <h4 class="text-16 mb-12">
                        {{ $dictionary.get('RackGuide.RackSummary.Heading') }}
                    </h4>
                    <div v-delegate-html-links
                         class="text-12 leading-normal wysiwyg wysiwyg-link-theme"
                         v-html="$dictionary.getAsMarkup('RackGuide.RackSummary.ButtonOpenForm.Description')"/>
                    <div class="flex mt-24">
                        <CallToAction class="ml-auto cta--green"
                                      :text="$dictionary.get('RackGuide.RackSummary.ButtonOpenForm')"
                                      @click="handleClickExternalRackGuideOpenForm(configurationId)"/>
                    </div>
                </div>
            </template>
        </RackConfiguratorPage>
        <CableGuideBlock v-else-if="isCableGuide && content" :content="content" :is-first-block="true"/>
        <ModalsContainer/>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick, defineAsyncComponent, useTemplateRef } from 'vue';
import { useRoute } from 'vue-router';
import {
    CableGuideBlockViewModel,
    ProductGuideBlockViewModel,
    RackConfiguratorPageViewModel
} from '@/types/contentServerContract';
import Api from '@/project/http/Api.service';
import { ProductGuideConfigurationViewModel } from '@/types/apiServerContract';
import ModalsContainer from '@/project/modals/ModalsContainer.vue';
import serverContextService from '@/core/serverContext.service';
import httpService from '@/core/http/http.service';
import dictionaryService from '@/core/translation/dictionary.service';
import { UrlQueryKey } from '@/core/urlQueryKey';
import urlHelperService from '@/core/urlHelper.service';
import viewportEventsService, { Viewport } from '@/core/responsive/viewport/viewportEvents.service';
import rackExternalRackConfiguratorGuideFormService
    from '@/project/app/client-pages/rack-configurator/rackExternalRackConfiguratorGuideForm.service';

const ProductGuideBlock = defineAsyncComponent(() => import('@/project/app/blocks/productguide/ProductGuideBlock.vue'));
const CableGuideBlock = defineAsyncComponent(() => import('@/project/app/blocks/cableguide/CableGuideBlock.vue'));
const CompatabilityGuideBlock = defineAsyncComponent(() => import('@/project/app/blocks/CompatabilityGuide/CompatabilityGuideBlock.vue'));
const RackConfiguratorPage = defineAsyncComponent(() => import('@/project/app/client-pages/rack-configurator/RackConfiguratorPage.vue'));

const route = useRoute();
const rootRef = useTemplateRef<HTMLElement | null>('rootRef');
const content = ref<ProductGuideBlockViewModel | CableGuideBlockViewModel | null>(null);
const additionalContent = ref<RackConfiguratorPageViewModel | undefined>(undefined);
const guideConfiguration = ref<ProductGuideConfigurationViewModel>();
const ExternalProductGuideResizeMessageType = 'productGuideResize';
const minimumGuideHeights = {
    compatibility: 390,
    cable: 800,
    rack: 590,
    default: 390
} as const;
const iframeHeightBuffer = 4;
const shrinkDelayInMs = 250;

let lastSentHeight = 0;
let resizeViewportCallback: ((viewport: Viewport, timestamp: number) => void) | undefined;
let shrinkTimeoutId: number | undefined;

const isCompatibilityGuide = computed((): boolean => {
    return !!content.value && content.value['guideId'] === 'compatibility';
});

const isCableGuide = computed(() => {
    return !!content.value && content.value['guideId'] === 'cable';
});

const isProductGuide = computed(() => {
    return !!content.value && content.value['guideId'] !== 'cable' && content.value['guideId'] !== 'compatibility' && content.value['guideId'] !== 'rack';
});

const isRackConfiguratorGuide = computed(() => {
    return !!content.value && !!additionalContent.value && content.value['guideId'] === 'rack';
})

const contentForProductGuide = computed(() => {
    return isProductGuide.value && content.value as ProductGuideBlockViewModel;
});

const handleClickExternalRackGuideOpenForm = async(configurationId: string | undefined) => {
    await rackExternalRackConfiguratorGuideFormService.show({
        sourceDomain: document.referrer,
        rackConfigurationId: configurationId || '',
        language: serverContextService.externalProductGuideSettings?.language || '',
        externalId: serverContextService.externalProductGuideSettings?.externalId
    });
};

const getGuideId = (): string => {
    const guideId = content.value && 'guideId' in content.value
        ? content.value.guideId
        : guideConfiguration.value?.type;

    return (guideId || 'all').toLowerCase();
};

const getMinimumGuideHeight = (): number => {
    const guideId = getGuideId();

    if (guideId === 'compatibility') {
        return minimumGuideHeights.compatibility;
    }

    if (guideId === 'cable') {
        return minimumGuideHeights.cable;
    }

    if (guideId === 'rack') {
        return minimumGuideHeights.rack;
    }

    return minimumGuideHeights.default;
};

const clearShrinkTimeout = () => {
    if (shrinkTimeoutId !== undefined) {
        window.clearTimeout(shrinkTimeoutId);
        shrinkTimeoutId = undefined;
    }
};

const getContentHeight = (): number => {
    const rootElement = rootRef.value;
    const body = document.body;
    const documentElement = document.documentElement;
    const minimumGuideHeight = getMinimumGuideHeight();

    if (rootElement) {
        const rootHeight = Math.max(
            rootElement.scrollHeight,
            rootElement.offsetHeight,
            Math.ceil(rootElement.getBoundingClientRect().height));
        const measuredHeight = rootHeight + iframeHeightBuffer;
        return Math.max(measuredHeight, minimumGuideHeight);
    }

    const fallbackHeight = Math.max(
        body?.scrollHeight ?? 0,
        body?.offsetHeight ?? 0,
        documentElement?.scrollHeight ?? 0,
        documentElement?.offsetHeight ?? 0,
        minimumGuideHeight) + iframeHeightBuffer;

    return fallbackHeight;
};

const postHeight = (height: number) => {
    if (window.parent === window) {
        return;
    }

    window.parent.postMessage(
        {
            type: ExternalProductGuideResizeMessageType,
            height,
            guideId: getGuideId()
        },
        '*');

    lastSentHeight = height;
};

const publishHeight = (allowDelayedShrink = true) => {
    const nextHeight = getContentHeight();

    if (nextHeight === lastSentHeight) {
        return;
    }

    if (!allowDelayedShrink || lastSentHeight === 0 || nextHeight > lastSentHeight) {
        clearShrinkTimeout();
        postHeight(nextHeight);
        return;
    }

    clearShrinkTimeout();
    shrinkTimeoutId = window.setTimeout(() => {
        const settledHeight = getContentHeight();

        if (settledHeight !== lastSentHeight) {
            postHeight(settledHeight);
        }
    }, shrinkDelayInMs);
};

const publishHeightOnNextTick = async(allowDelayedShrink = true) => {
    await nextTick();
    publishHeight(allowDelayedShrink);
};

const createContent = async(): Promise<ProductGuideBlockViewModel | CableGuideBlockViewModel> => {
    let productPageSize = parseInt(urlHelperService.getQueryParam(UrlQueryKey.productGuideProductPageSize) || '30', 10);
    if (isNaN(productPageSize)) {
        productPageSize = 30;
    }

    const guideId = (urlHelperService.getQueryParam(UrlQueryKey.productGuideGuideId) || guideConfiguration.value?.type || 'all').toLowerCase();
    const hideStock = (guideConfiguration.value?.hideStock ?? (urlHelperService.getQueryParam(UrlQueryKey.hideStock) === 'true'));
    const showFacets = guideConfiguration.value?.showFacets ?? (!(urlHelperService.getQueryParam(UrlQueryKey.hideFacets)) || false);
    const startPage = guideConfiguration.value?.startPage ?? urlHelperService.getQueryParam(UrlQueryKey.productGuideModeKey);
    const externalProductLinkText = guideConfiguration.value?.externalProductLinkText ?? urlHelperService.getQueryParam(UrlQueryKey.productGuideExternalButtonText);
    const externalProductLink = guideConfiguration.value?.externalProductLink ?? urlHelperService.getQueryParam(UrlQueryKey.productGuideExternalButtonLink);
    const externalStylesheet = guideConfiguration.value?.externalStylesheet ?? urlHelperService.getQueryParam(UrlQueryKey.css);
    const language = guideConfiguration.value?.language ?? (urlHelperService.getQueryParam(UrlQueryKey.language) || undefined);
    const theme = guideConfiguration.value?.theme?.toLowerCase() ?? (urlHelperService.getQueryParam(UrlQueryKey.theme) || '');
    const allowedBrands = guideConfiguration.value?.allowedBrands ?? [];

    serverContextService.externalProductGuideSettings = {
        hideStock,
        externalProductLink,
        externalProductLinkText,
        externalStylesheet,
        language,
        theme,
        showFacets,
        startPage,
        allowedBrands,
        guideId,
        externalId: urlHelperService.getQueryParam(UrlQueryKey.productGuideExternalId)
    };

    updateExternalStylesheet();
    await updateDictionary();

    return {
        guideId,
        productPageSize: productPageSize,
        appendToPreviousBlock: false,
        startInStepByStep: startPage === 'step-by-step'
    } as ProductGuideBlockViewModel;
};

const createAdditionalContent = async(contentUrl): Promise<RackConfiguratorPageViewModel | undefined> => {
    if (contentUrl) {
        try {
            const umbracoContent = await httpService.getPageParallel(`${contentUrl}?culture=${serverContextService.externalProductGuideSettings?.language}`);
            if (umbracoContent) {
                return umbracoContent?.jsonContent?.content as RackConfiguratorPageViewModel | undefined;
            }
        } catch (error) {
            console.error('Error fetching Umbraco content:', error);
        }
    }
    return undefined;
};

const getConfiguration = async(externalId: string) => {
    guideConfiguration.value = await Api.productGuide.getConfiguration(externalId);
    const overrideProductUrl = urlHelperService.getQueryParam(UrlQueryKey.productGuideOverrideProductUrl);

    if (overrideProductUrl) {
        guideConfiguration.value.externalProductLink = overrideProductUrl;
    }
};

const updateExternalStylesheet = () => {
    const externalStylesheet = serverContextService.externalProductGuideSettings?.externalStylesheet;

    if (externalStylesheet) {
        let linkElement = document.querySelector('#productGuideCss');
        if (!linkElement) {
            linkElement = document.createElement('link');
            linkElement.id = 'productGuideCss';
            linkElement.setAttribute('rel', 'stylesheet');
            linkElement.setAttribute('type', 'text/css');
            document.head.appendChild(linkElement);
        }

        linkElement.setAttribute('href', externalStylesheet);
    }
};

const updateDictionary = async() => {
    const language = serverContextService.externalProductGuideSettings?.language;

    if (language) {
        const dictionary = await httpService.getRaw<{[key:string]: string }>('/app/dictionary?siteBaseUrl=/' + language);
        if (dictionary) {
            window.servercontext.dictionary = dictionary;
            dictionaryService.reinitialize();
        }
    }
};

const init = async() => {
    const externalId = urlHelperService.getQueryParam(UrlQueryKey.productGuideExternalId);
    if (externalId) {
        await getConfiguration(externalId);
    }
    content.value = await createContent();

    if (guideConfiguration.value && 'additionalContentUrl' in guideConfiguration.value && guideConfiguration.value.additionalContentUrl) {
        additionalContent.value = await createAdditionalContent(guideConfiguration.value?.additionalContentUrl);
    }

    await publishHeightOnNextTick(false);
};

watch(() => route, () => {
    const guideId = (content.value as ProductGuideBlockViewModel)?.guideId;
    if (!guideId) {
        createContent();
    }
});

watch([content, additionalContent], () => {
    void publishHeightOnNextTick();
});

onMounted(() => {
    resizeViewportCallback = () => {
        publishHeight();
    };

    viewportEventsService.setCallback(resizeViewportCallback);
    void init();
    void publishHeightOnNextTick(false);
});

onUnmounted(() => {
    clearShrinkTimeout();

    if (resizeViewportCallback) {
        viewportEventsService.removeCallback(resizeViewportCallback);
    }
});
</script>
